import { grey } from '@mui/material/colors';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import useScopedCartActions from '../../../hooks/cartScope/useScopedCartActions.hooks';
import { selectIsAgentActionBlocked } from '../../../redux/features/agentMode/agentMode.selector';
import { ParsedMenuItem, ParsedModifierGroup } from '../../../types/menu';
import { BLOCKED_AFFORDANCE_STYLE } from '../../../utils/agentModeAffordance';
import { CartItem, CartModifierGroup } from '../../../utils/cart';
import Colors from '../../../utils/color';
import { checkItemInCartItemTree } from '../../../utils/menu';
import { MOD_GROUP_ROW_INDENT_PX } from '../components/ModGroupRow';
import { getMenuUnificationFeatureFlag } from '../../../utils/session-storage';
import { useDisplayPreference } from '../../Layout/DisplayPreferenceContext';
import { sortAndFilterMenuItems } from '../util/helper';
import { SelectMenuItemModifier } from './modGroup.props';
import { applyModGroupDefaults } from './modGroup.utils';
import { selectUnavailableItemsByName } from '../../../selectors/menu';
import {
  addToSlots,
  describeRepeatedSlots,
  fillEmptySlots,
  paintSlots,
  removeFromSlots,
  RepeatedSlotSet,
  SlotValue,
} from '../util/repeatedSlots.utils';
import useRepeatedSlotsWrite, {
  RepeatedSlotChange,
} from './useRepeatedSlotsWrite.hooks';

/**
 * The repeated-slot panel: one panel in place of N interchangeable single-pick
 * rows (Baker's Dozen `bagel_choice_1..13`).
 *
 * - Tiles: a tap adds one of that option to the next empty slot AND makes it
 *   the brush. `×N` shows how many slots hold it; `−` takes one away.
 * - Strip: one cell per slot. A click paints that cell with the brush; a drag
 *   across cells paints every cell it crosses, committed as ONE write when the
 *   pointer lifts (HITL-1719).
 * - Keys 1–9 tap the first nine tiles, but only while the panel has focus —
 *   the agent clicks in, the panel never takes focus itself, and a digit typed
 *   anywhere else (the OmniBar quantity) never reaches it (HITL-1719).
 * - Fill all: every empty slot gets the brush. Undo: back one gesture per
 *   press, as far back as the panel's first gesture on this line.
 * - The strip always reads grouped (PPPPPP EEEE SSS), empties last.
 * - The `(N / 13)` beside the name and every empty cell stay red until the set
 *   is full, because
 *   sendOrder drops the whole item while any required slot is empty.
 *
 * Slots are read from the cart on every render, never cached, so whatever the
 * AI or another surface puts in them shows up as it lands. Every gesture is
 * one write through useRepeatedSlotsWrite (one dispatch, one intervention).
 *
 * Styling is inline, from the 7.x centre pane's own values (ModGroupRow,
 * OptionDrawer, ModOptionRow), so no JSS sheet is added.
 */

const FLASH_MS = 600;
const UNDO_LIMIT = 50;

const styles = {
  // The same 4px a side ModGroup's `modGroupCompact` gives every row. A
  // margin, not padding: CartItemDetail's tab panel forces
  // `padding: 0 !important` on each of its direct children, which this root is.
  root: { margin: '0 4px', outline: 'none' } as React.CSSProperties,
  // Then the rows' caret column (8px caret + 4px gap), so the label lines up
  // with every other group's name and the tiles sit off the card edge.
  // Mirrored on the right. On an inner box, where the tab panel's rule
  // cannot reach.
  body: {
    padding: `3px ${MOD_GROUP_ROW_INDENT_PX}px`,
  } as React.CSSProperties,
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: '12px',
  } as React.CSSProperties,
  // ModGroupRow's `key` rule, so the panel's name reads exactly like
  // `bagging_slicing_plates` and every other group name in the pane.
  label: {
    flex: '0 1 auto',
    minWidth: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: '12px',
    fontWeight: 500,
  } as React.CSSProperties,
  tiles: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: 4,
    margin: '4px 0',
  } as React.CSSProperties,
  strip: {
    display: 'grid',
    gap: 2,
    margin: '4px 0',
  } as React.CSSProperties,
};

const tileStyle = (isBrush: boolean, is86ed: boolean): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  height: 24,
  padding: '0 6px',
  boxSizing: 'border-box',
  borderRadius: 2,
  border: `1px solid ${isBrush ? Colors.mineShaft : grey[400]}`,
  backgroundColor: isBrush ? Colors.mineShaft : 'transparent',
  color: is86ed ? Colors.Thunderbird : isBrush ? Colors.white : Colors.daintree,
  textDecoration: is86ed ? 'line-through' : 'none',
  fontSize: '13px',
  fontWeight: isBrush ? 700 : 400,
  cursor: is86ed ? 'default' : 'pointer',
  userSelect: 'none',
});

const countChipStyle: React.CSSProperties = {
  flexShrink: 0,
  fontSize: '10px',
  fontWeight: 700,
  lineHeight: '14px',
  padding: '0 4px',
  borderRadius: 7,
  backgroundColor: Colors.alto,
  color: Colors.daintree,
};

const cellStyle = (
  isEmpty: boolean,
  isRequired: boolean
): React.CSSProperties => ({
  height: 24,
  minWidth: 0,
  boxSizing: 'border-box',
  borderRadius: 2,
  border: `1px solid ${isEmpty && isRequired ? Colors.Thunderbird : grey[400]}`,
  backgroundColor: isEmpty ? 'transparent' : Colors.alto,
  color: Colors.daintree,
  fontSize: '11px',
  fontWeight: 700,
  // Plain text centring rather than flex: a flex item wider than a narrow
  // cell overflows to the right, so the initials drifted off centre.
  // 22px = 24px less the two 1px borders, which centres it vertically too.
  textAlign: 'center',
  lineHeight: '22px',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  userSelect: 'none',
});

const actionStyle = (enabled: boolean): React.CSSProperties => ({
  border: `1px solid ${grey[400]}`,
  borderRadius: 2,
  background: 'transparent',
  color: enabled ? Colors.daintree : Colors.silverChalice,
  // Sized to sit in the header row beside the counter without growing it.
  fontSize: '11px',
  lineHeight: '16px',
  padding: '0 6px',
  cursor: enabled ? 'pointer' : 'default',
});

/**
 * Was this focus reached from the keyboard? `:focus-visible` is the browser's
 * own answer; an engine without it (jsdom) answers no.
 */
const isKeyboardFocus = (target: EventTarget) => {
  try {
    return (target as Element).matches(':focus-visible');
  } catch {
    return false;
  }
};

/** `Cinnamon Raisin` → `CR`, `Plain` → `PL`: what fits in a 1/13 cell. */
export const slotInitials = (name: string) => {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length > 1) return (words[0][0] + words[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

/**
 * Applies one slot's menu defaults the way its row would have: once, while the
 * cart has no entry for the group, through CartItemDetail's own selection
 * callback (so the default queue, scope and "announce nothing" rule all hold).
 * Renders nothing.
 */
const SlotDefaults = ({
  cartItem,
  modGroup,
  selectMenuItemModifier,
}: {
  cartItem: CartItem;
  modGroup: ParsedModifierGroup;
  selectMenuItemModifier: SelectMenuItemModifier;
}) => {
  const unavailableItemsByName = useSelector(selectUnavailableItemsByName);
  const cartModGroup = checkItemInCartItemTree({
    cartItem,
    pathToItem: modGroup.id,
  });
  useEffect(() => {
    if (cartModGroup || !modGroup.defaultSelectedItemIds?.length) return;
    applyModGroupDefaults({
      cartItem,
      modGroup,
      pathToItem: modGroup.id,
      // Top-level slots only, whose path has no parent node.
      parentModifier: '',
      isBrandMenu: getMenuUnificationFeatureFlag(),
      unavailableItemsByName,
      selectMenuItemModifier,
    });
    // Same trigger as ModGroup's own default effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartModGroup, modGroup.id]);
  return null;
};

interface RepeatedSlotPanelProps {
  cartItem: CartItem;
  slotSet: RepeatedSlotSet;
  selectMenuItemModifier: SelectMenuItemModifier;
}

const RepeatedSlotPanel = ({
  cartItem,
  slotSet,
  selectMenuItemModifier,
}: RepeatedSlotPanelProps) => {
  const write = useRepeatedSlotsWrite(cartItem);
  const { isDraft } = useScopedCartActions();
  const isAgentActionBlocked = useSelector(selectIsAgentActionBlocked);
  const isReadOnly = isAgentActionBlocked && !isDraft;
  // HITL-1696: the agent's CAPITALS choice, applied as ModGroupRow applies it.
  const { pref: displayPref } = useDisplayPreference();
  const isCaps = displayPref.cpCase === 'caps';

  const { groups, stem, isRequired, keepsPositions } = slotSet;

  // Same order the rows' drawer uses, so the tiles read like the options did.
  const options: ParsedMenuItem[] = useMemo(() => {
    const menuItems = groups[0].menuItems;
    return getMenuUnificationFeatureFlag()
      ? Object.values(menuItems)
      : sortAndFilterMenuItems(menuItems);
  }, [groups]);
  const optionOrder = useMemo(
    () => options.map(({ itemId }) => itemId),
    [options]
  );
  const optionsById = useMemo(
    () => Object.fromEntries(options.map((option) => [option.itemId, option])),
    [options]
  );

  const slots: SlotValue[] = groups.map((modGroup) => {
    const cartModGroup = checkItemInCartItemTree({
      cartItem,
      pathToItem: modGroup.id,
    }) as CartModifierGroup | null;
    return Object.values(cartModGroup?.selectedItems ?? {})[0]?.itemId;
  });
  const filled = slots.filter(Boolean).length;
  const isFull = filled === slots.length;
  // Only a required set is ever "unmet": an optional set may stay empty.
  const isUnmet = isRequired && !isFull;

  const [brush, setBrush] = useState<string | undefined>();
  // Every gesture's "before", newest last, so Undo can be pressed again and
  // again back to where the agent started. Capped so a long session cannot
  // grow it without bound.
  const [undoStack, setUndoStack] = useState<SlotValue[][]>([]);
  const [isFlashing, setIsFlashing] = useState(false);
  const flashTimer = useRef<ReturnType<typeof setTimeout>>();
  const clearFlashTimer = () => {
    if (flashTimer.current) clearTimeout(flashTimer.current);
  };
  useEffect(() => () => clearFlashTimer(), []);

  const flash = useCallback(() => {
    clearFlashTimer();
    setIsFlashing(true);
    flashTimer.current = setTimeout(() => setIsFlashing(false), FLASH_MS);
  }, []);

  // A different line is a different history: Undo must never write one
  // item's earlier slots onto another.
  useEffect(() => setUndoStack([]), [cartItem.cartItemId]);

  const [isFocused, setIsFocused] = useState(false);
  // Clicking a tile focuses the panel too, so focus alone would flash the
  // 1–9 hints on every mouse press. They show once the keys are in use: the
  // panel was reached from the keyboard (Tab), or a digit has been pressed.
  const [isUsingKeys, setIsUsingKeys] = useState(false);
  const showKeyHints = isFocused && isUsingKeys;
  // The cells a drag has crossed so far. Painted as a preview, written once on
  // pointer-up so a stroke is one dispatch, one `order` and one Undo step.
  const [stroke, setStroke] = useState<number[] | null>(null);

  /** Writes `target` over the current slots; remembers them for Undo. */
  const commit = (target: SlotValue[], { recordUndo = true } = {}) => {
    const changes: RepeatedSlotChange[] = [];
    groups.forEach((modGroup, index) => {
      if (slots[index] === target[index]) return;
      changes.push({
        modGroup,
        from: slots[index] ? optionsById[slots[index]!] : undefined,
        to: target[index] ? optionsById[target[index]!] : undefined,
      });
    });
    if (!changes.length) return;
    const summary = describeRepeatedSlots(
      cartItem.name,
      target.map((id) => (id ? optionsById[id]?.name : undefined))
    );
    if (!write(changes, summary)) return;
    setUndoStack((stack) =>
      recordUndo ? [...stack, slots].slice(-UNDO_LIMIT) : stack.slice(0, -1)
    );
  };

  const onTileClick = (option: ParsedMenuItem) => {
    if (isReadOnly || option.available === false) return;
    // The brush moves even when the set is full, so a strip a mix or the AI
    // filled can still be repainted.
    setBrush(option.itemId);
    const target = addToSlots(
      slots,
      option.itemId,
      optionOrder,
      keepsPositions
    );
    if (!target) {
      flash();
      return;
    }
    commit(target);
  };

  const onRemoveClick = (event: React.MouseEvent, option: ParsedMenuItem) => {
    event.stopPropagation();
    if (isReadOnly) return;
    const target = removeFromSlots(
      slots,
      option.itemId,
      optionOrder,
      keepsPositions
    );
    if (target) commit(target);
  };

  const onCellPointerDown = (index: number, event: React.PointerEvent) => {
    if (isReadOnly || !brush || event.button > 0) return;
    event.preventDefault();
    // Touch pointers are captured by the element they start on, which would
    // keep every other cell from ever seeing pointerenter.
    const target = event.target as Element;
    if (target.hasPointerCapture?.(event.pointerId)) {
      target.releasePointerCapture(event.pointerId);
    }
    setStroke([index]);
  };

  const onCellPointerEnter = (index: number) => {
    setStroke((current) =>
      current && !current.includes(index) ? [...current, index] : current
    );
  };

  // The pointer can lift anywhere, so listen on the window for the length of
  // the stroke. A ref keeps the listener on the latest slots and brush.
  const finishStrokeRef = useRef<(commitIt: boolean) => void>(() => {});
  finishStrokeRef.current = (commitIt: boolean) => {
    if (commitIt && stroke && brush) {
      // On an optional set, clicking a cell that already holds the brush
      // empties it: the only way to clear one unit's pick in place.
      const isClear =
        !isRequired && stroke.length === 1 && slots[stroke[0]] === brush;
      commit(
        paintSlots(
          slots,
          stroke,
          isClear ? undefined : brush,
          optionOrder,
          keepsPositions
        )
      );
    }
    setStroke(null);
  };
  const isStroking = stroke !== null;
  useEffect(() => {
    if (!isStroking) return undefined;
    const onUp = () => finishStrokeRef.current(true);
    const onCancel = () => finishStrokeRef.current(false);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    return () => {
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
    };
  }, [isStroking]);

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    if (!/^[1-9]$/.test(event.key)) return;
    const option = options[Number(event.key) - 1];
    if (!option) return;
    event.preventDefault();
    event.stopPropagation();
    setIsUsingKeys(true);
    onTileClick(option);
  };

  const canFill = !isReadOnly && !!brush && !isFull;
  const onFillAll = () => {
    if (canFill)
      commit(fillEmptySlots(slots, brush!, optionOrder, keepsPositions));
  };

  const canUndo = !isReadOnly && undoStack.length > 0;
  const onUndo = () => {
    if (canUndo) commit(undoStack[undoStack.length - 1], { recordUndo: false });
  };

  const counts = slots.reduce<Record<string, number>>((acc, id) => {
    if (id) acc[id] = (acc[id] || 0) + 1;
    return acc;
  }, {});

  return (
    <div
      style={{
        ...styles.root,
        ...(isReadOnly ? BLOCKED_AFFORDANCE_STYLE : null),
      }}
      data-testid="repeated-slot-panel"
      // Focusable so 1–9 can be scoped to it; never focused programmatically.
      tabIndex={0}
      onKeyDown={onKeyDown}
      onFocus={(event) => {
        setIsFocused(true);
        if (isKeyboardFocus(event.target)) setIsUsingKeys(true);
      }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setIsFocused(false);
          setIsUsingKeys(false);
        }
      }}
    >
      <div style={styles.body} data-testid="repeated-slot-body">
        {groups.map((modGroup) => (
          <SlotDefaults
            key={modGroup.id}
            cartItem={cartItem}
            modGroup={modGroup}
            selectMenuItemModifier={selectMenuItemModifier}
          />
        ))}
        <div style={styles.header}>
          <span
            data-testid="repeated-slot-label"
            style={{
              ...styles.label,
              // As a row: grey once satisfied, red with `!` while unmet.
              color: isUnmet ? Colors.Thunderbird : Colors.silverChalice,
              ...(isCaps ? { textTransform: 'uppercase' } : null),
            }}
          >
            {isUnmet ? `! ${stem}` : stem}
            {/*
              Where a row prints its `(select 1)`: same 11px / 400, same
              colour as the name, so the count reads as the group's own
              requirement rather than a separate badge.
            */}
            <span
              data-testid="repeated-slot-counter"
              style={{
                fontSize: '11px',
                fontWeight: 400,
                backgroundColor: isFlashing ? Colors.alto : 'transparent',
                borderRadius: 7,
                transition: 'background-color 120ms ease',
              }}
            >
              {` (${filled} / ${slots.length})`}
            </span>
          </span>
          <button
            type="button"
            style={{ ...actionStyle(canFill), marginLeft: 'auto' }}
            disabled={!canFill}
            onClick={onFillAll}
          >
            Fill all
          </button>
          <button
            type="button"
            style={actionStyle(canUndo)}
            disabled={!canUndo}
            onClick={onUndo}
          >
            Undo
          </button>
        </div>

        <div style={styles.tiles}>
          {options.map((option, optionIndex) => {
            const is86ed = option.available === false;
            const isBrush = brush === option.itemId;
            const count = counts[option.itemId] || 0;
            return (
              <div
                key={option.itemId}
                role="button"
                tabIndex={-1}
                aria-disabled={is86ed}
                aria-pressed={isBrush}
                data-testid="repeated-slot-tile"
                data-option={option.itemId}
                style={{
                  ...tileStyle(isBrush, is86ed),
                  ...(isReadOnly ? BLOCKED_AFFORDANCE_STYLE : null),
                }}
                onClick={() => onTileClick(option)}
              >
                {showKeyHints && optionIndex < 9 && (
                  <span
                    data-testid="repeated-slot-key"
                    style={{
                      flexShrink: 0,
                      fontSize: '11px',
                      color: isBrush ? Colors.white : Colors.silverChalice,
                    }}
                  >
                    {optionIndex + 1}
                  </span>
                )}
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {option.name}
                </span>
                {count > 0 && (
                  <>
                    <span style={countChipStyle}>×{count}</span>
                    <span
                      role="button"
                      aria-label={`Remove one ${option.name}`}
                      data-testid="repeated-slot-remove"
                      style={{ fontWeight: 700, padding: '0 2px' }}
                      onClick={(event) => onRemoveClick(event, option)}
                    >
                      −
                    </span>
                  </>
                )}
              </div>
            );
          })}
        </div>

        <div
          style={{
            ...styles.strip,
            gridTemplateColumns: `repeat(${slots.length}, minmax(0, 1fr))`,
          }}
        >
          {slots.map((slotId, index) => {
            const id = stroke?.includes(index) ? brush : slotId;
            const name = id ? optionsById[id]?.name ?? id : undefined;
            return (
              <div
                key={groups[index].id}
                title={name ? `${index + 1}: ${name}` : `${index + 1}: empty`}
                data-testid="repeated-slot-cell"
                data-value={slotId ?? ''}
                style={{
                  ...cellStyle(!id, isRequired),
                  ...(isReadOnly ? BLOCKED_AFFORDANCE_STYLE : null),
                }}
                onPointerDown={(event) => onCellPointerDown(index, event)}
                onPointerEnter={() => onCellPointerEnter(index)}
              >
                {name ? slotInitials(name) : index + 1}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default RepeatedSlotPanel;
